import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { connect } from "react-redux";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import {
  listRequest,
  setQuery,
  deleteRequest,
} from "../../../../store/slices/audienceSlice";
import List from "../../../common/list";
import { Dialog } from "primereact/dialog";
import AudienceUserList from "./AudienceUserList";
import { TextInput } from "../../../common/fields";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import useMobileView from "../../../common/hooks/useMobileView";

const AudienceList = ({
  labels,
  user,
  audiences,
  listRequest,
  action,
  error,
  query,
  requesting,
  setQuery,
  totalCount,
  isFromAssigment = false,
  deleteRequest,
}) => {
  const navigate = useNavigate();
  const [isUserListDialogVisible, setUserListDialogVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [namePattern, setNamePattern] = useState("");
  const [createdByNamePattern, setCreatedByNamePattern] = useState("");

  const intl = useIntl();

  const { isMobile } = useMobileView();

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.CreateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.DeleteScope
  );

  const fetchList = useCallback(() => {
    listRequest({
      ...query,
    });
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (
      !requesting &&
      !error &&
      (action === "create" || action === "update" || action === "delete")
    ) {
      fetchList();
    }
  }, [action, requesting, error]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query?.namePattern !== namePattern) {
        setQuery({ ...query, namePattern, pageNo: 1 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [namePattern, setQuery, query]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query?.createdBy !== createdByNamePattern) {
        setQuery({ ...query, createdBy: createdByNamePattern, pageNo: 1 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [createdByNamePattern, setQuery, query]);

  const handleAddAudience = () => {
    navigate(RouteConstants.APP_ROUTE_ADMINISTRATION_AUDIENCES_ADD);
  };

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const onUserList = (id) => {
    setSelectedId(id);
    setUserListDialogVisible(true);
  };

  const onClone = (id) => {
    navigate(
      `${RouteConstants.APP_ROUTE_ADMINISTRATION_AUDIENCES_ADD}?clone=${id}`
    );
  };

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: data.name,
          object: intl.formatMessage({ id: "audience" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        deleteRequest({ id: data.id });
      },
      reject: () => {},
    });
  };

  let otherActions = [
    {
      label: "view_users",
      icon: "pi pi-users",
      isAllowed: true,
      action: (data) => onUserList(data.id),
    },
  ];

  if (!isFromAssigment) {
    otherActions.push({
      label: "clone",
      icon: "pi pi-clone",
      isAllowed: canAdd,
      action: (data) => onClone(data.id),
    });
    otherActions.push({
      label: "delete",
      icon: "pi-trash",
      isAllowed: canDelete,
      additionalCheckNotAllowedMessage: "no_permission_delete_message_cohort",
      additionalCheckForAllowed: (data) => data.canBeDeleted,
      action: (data) => onDeleteAction(data),
    });
  }

  return (
    <>
      <div>
        {(!isFromAssigment && (
          <CustomBreadCrumbs labels={labels} navigate={navigate} />
        )) ||
          null}
        <div
          className="edexlms-page-content"
          style={isFromAssigment ? { padding: 0 } : {}}
        >
          {(!isFromAssigment && (
            <div className="edexlms-page-header">
              <div className="edexlms-page-title">
                {intl.formatMessage({ id: "audiences" })}
              </div>
              <div className="edexlms-page-actions">
                <Button
                  icon="pi pi-plus"
                  onClick={handleAddAudience}
                  size="small"
                  label={intl.formatMessage({ id: "add_audience" })}
                  type="button"
                  disabled={!canAdd}
                  tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                  tooltip={
                    canAdd ? "" : intl.formatMessage({ id: "no_permission" })
                  }
                />
              </div>
            </div>
          )) ||
            null}
          <div className="edexlms-page-body">
            <div className="edexlms-search-filter-bar">
              <TextInput
                iconPosition="left"
                icon="pi-search"
                placeholder={intl.formatMessage({
                  id: "search_audiences",
                })}
                input={{
                  name: "search",
                  value: namePattern,
                  onChange: (e) => setNamePattern(e.target.value),
                }}
              />
              <TextInput
                iconPosition="left"
                icon="pi-search"
                placeholder={intl.formatMessage({
                  id: "search_created_by",
                })}
                input={{
                  name: "search",
                  value: createdByNamePattern,
                  onChange: (e) => setCreatedByNamePattern(e.target.value),
                }}
              />
            </div>
            <List
              pageQuery={query}
              handlePageChange={handlePageChange}
              handleOnSortChange={handleOnSortChange}
              loading={action === "list" && requesting}
              data={audiences || []}
              totalRecords={totalCount}
              handleRowClick={(data) => {
                if (!isFromAssigment) {
                  navigate(
                    `${RouteConstants.APP_ROUTE_ADMINISTRATION_AUDIENCES_EDIT.replace(
                      ":id",
                      data?.data?.id
                    )}`
                  );
                } else {
                  navigate(
                    `${RouteConstants.APP_ROUTE_ASSIGNMENTS_AUDIENCE.replace(
                      ":id",
                      data?.data?.id
                    )}`
                  );
                }
              }}
              selectionMode=""
              columns={[
                {
                  field: "name",
                  header: isFromAssigment ? "audience_name" : "name",
                  sortable: true,
                },
                {
                  field: "userCount",
                  header: "user_count",
                  sortable: true,
                },
                {
                  field: "createdByUser",
                  header: "created_by",
                  sortable: false,
                  body: ListCellRenders.CreatedBy,
                },
                {
                  field: "createdAt",
                  header: "created_at",
                  sortable: true,
                  body: ListCellRenders.DateTime("createdAt"),
                },
                {
                  field: "updatedAt",
                  header: "updated_at",
                  sortable: true,
                  body: ListCellRenders.DateTime("updatedAt"),
                },
                {
                  field: "actions",
                  headerIcon: "pi-cog",
                  align: "center",
                  body: ListCellRenders.OtherActions(otherActions),
                },
              ]}
            />
          </div>
        </div>
      </div>
      <Dialog
        style={{
          width: isMobile ? "90vw" : "70vw",
          minWidth: isMobile ? "auto" : "800px",
          height: "80vh",
          minHeight: "500px",
        }}
        visible={isUserListDialogVisible}
        onHide={() => setUserListDialogVisible(false)}
        header={intl.formatMessage(
          { id: "audience_users" },
          { name: audiences.find((a) => a.id === selectedId)?.name }
        )}
      >
        <AudienceUserList audienceId={selectedId} />
      </Dialog>
      <ConfirmDialog />
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state?.self?.user,
  audiences: state?.audiences?.data?.list || [],
  action: state?.audiences?.action,
  requesting: state?.audiences?.requesting,
  error: state?.audiences?.error,
  query: state?.audiences?.query,
  totalCount: state?.audiences?.data?.totalCount,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (query) => dispatch(listRequest(query)),
  setQuery: (query) => dispatch(setQuery(query)),
  deleteRequest: (data) => dispatch(deleteRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AudienceList);
